import { RefObject } from 'react'
import { applyChange } from '../utils'
import { toggleUnorderedListAction } from './toggleUnorderedListAction'
import { toggleOrderedListAction } from './toggleOrderedListAction'
import { toggleQuoteAction } from './toggleQuoteAction'
import { indentByConstantAction } from './indentByConstantAction'
import { unindentListAction } from './unindentListAction'
import { newListItemAction } from './newListItemAction'
import { continueListItemAction } from './continueListItemAction'
import { clearListItemAction } from './clearListItemAction'
import { newQuoteLineAction } from './newQuoteLineAction'
import type { ContinueListItemOptions } from './continueListItemAction'
import { duplicateAction } from './duplicateAction'
import { indentListAction } from './indentListAction'
import { markdownConfig } from '../../config'
import { insertTextAction } from './insertTextAction'
import {
  toggleBoldAction,
  toggleInlineCodeAction,
  toggleItalicAction,
  toggleStrikeAction,
} from './toggleMarkingActions'

export type MarkdownActions = {
  toggleBold: () => void
  toggleItalic: () => void
  toggleStrike: () => void
  toggleUnorderedList: () => void
  toggleOrderedList: () => void
  toggleQuote: () => void
  toggleInlineCode: () => void
  insertCodeBlock: () => void
  insertTable: () => void
  insertLink: () => void
  insertImage: (url?: string, altText?: string) => void
  insertImages: (images: { url: string; altText?: string }[]) => void
  insertText: (text: string) => void
  duplicate: () => void
  indent: () => boolean
  unindent: () => boolean
  newListItem: (increaseNumber?: boolean) => boolean
  continueListItem: (options?: ContinueListItemOptions) => boolean
  clearListItem: () => boolean
  newQuoteLine: () => boolean
}

export const createMarkdownActions = (
  textareaRef: RefObject<HTMLTextAreaElement>,
  value: string,
  onChange: (val: string) => void,
): MarkdownActions => {
  return {
    toggleBold: () => toggleBoldAction(textareaRef, value, onChange),
    toggleItalic: () => toggleItalicAction(textareaRef, value, onChange),
    toggleStrike: () => toggleStrikeAction(textareaRef, value, onChange),
    toggleInlineCode: () =>
      toggleInlineCodeAction(textareaRef, value, onChange),
    insertCodeBlock() {
      const el = textareaRef.current
      if (!el) return
      const s = el.selectionStart ?? 0
      const e = el.selectionEnd ?? s
      const before = value.slice(0, s)
      const selected = value.slice(s, e)
      const after = value.slice(e)
      const codeText = selected || 'code'
      const insert = '```\n' + codeText + '\n```'
      const next = before + insert + after
      const start = before.length + 4
      const end = start + codeText.length
      applyChange(textareaRef, onChange, next, start, end)
    },
    insertTable() {
      const template = `| Header1 | Header2 | Header3 |
| -------- | -------- | -------- |
| Cell1   | Cell2   | Cell3   |
| Cell4   | Cell5   | Cell6   |`
      insertTextAction(textareaRef, value, onChange, template, {
        start: 2,
        end: 2 + 'Header1'.length,
      })
    },
    toggleUnorderedList: () =>
      toggleUnorderedListAction(textareaRef, value, onChange),
    toggleOrderedList: () =>
      toggleOrderedListAction(textareaRef, value, onChange, {
        incrementIndex: markdownConfig.incrementOrderedListMark,
      }),
    toggleQuote: () => toggleQuoteAction(textareaRef, value, onChange),
    insertLink: () => {
      const el = textareaRef.current
      if (!el) return
      const s = el.selectionStart ?? 0
      const e = el.selectionEnd ?? s
      const before = value.slice(0, s)
      const selected = value.slice(s, e) || 'link'
      const after = value.slice(e)
      const url = 'https://'
      const insert = `[${selected}](${url})`
      const next = before + insert + after
      // select 'https://' between parentheses
      const selectionStart = before.length + selected.length + 3
      const selectionEnd = selectionStart + url.length
      applyChange(textareaRef, onChange, next, selectionStart, selectionEnd)
    },
    insertImage: (url = '', altText = '') => {
      const insertedUrl = url || 'https://'
      const insert = `![${altText}](${insertedUrl})`
      const posInsideParens = `![${altText}](`.length

      insertTextAction(textareaRef, value, onChange, insert, {
        start: posInsideParens,
        end: posInsideParens + insertedUrl.length,
      })
    },
    insertImages: (images: { url: string; altText?: string }[]) => {
      const toInsert = (images || [])
        .map((img) => {
          if (!img || !img.url) return ''
          const alt = img.altText || ''
          return `![${alt}](${img.url})`
        })
        .filter(Boolean)
        .join('\n')
      if (!toInsert) return
      insertTextAction(textareaRef, value, onChange, toInsert)
    },
    insertText: (text: string) =>
      insertTextAction(textareaRef, value, onChange, text),
    duplicate: () => duplicateAction(textareaRef, value, onChange),
    indent: () =>
      indentListAction(textareaRef, value, onChange) ||
      indentByConstantAction(
        textareaRef,
        value,
        onChange,
        markdownConfig.defaultIndent,
      ),
    unindent: () =>
      unindentListAction(textareaRef, value, onChange) ||
      indentByConstantAction(
        textareaRef,
        value,
        onChange,
        -markdownConfig.defaultIndent,
      ),
    newListItem: (increaseNumber?: boolean) =>
      newListItemAction(textareaRef, value, onChange, increaseNumber),
    continueListItem: (options?: ContinueListItemOptions) =>
      continueListItemAction(textareaRef, value, onChange, options),
    clearListItem: () => clearListItemAction(textareaRef, value, onChange),
    newQuoteLine: () => newQuoteLineAction(textareaRef, value, onChange),
  }
}
